// Add or edit a self-note (architecture sprint-15 §9.4; owner decision 2). A self-note is about the person
// writing it. Project from GET /api/notes/mine/projects (locked when editing); Date defaults to today and
// can't be in the future; Shift only on a shift project, with the open shift as the default for today and
// "No shift" allowed; Description 1–2000 characters with a counter. The API re-checks everything.
import { todayIn, type LocalDate } from '@shared/calendar';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useCreateOwnNote, useOwnNoteProjects, useOwnShiftOptions, useUpdateOwnNote } from '@/hooks/use-own-notes';
import { ApiClientError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { timeRange } from '@/lib/shift-format';
import { NOTE_MAX_LENGTH, type OwnNoteDto } from '@/types/notes';
import { FUTURE_DATE_MESSAGE } from './NoteDialog';

export const SELF_NOTE_REVIEWED_HINT = "Your project's Supervisors and Admins can read this note and will review it.";
export const SELF_NOTE_UNREVIEWED_HINT = "Your project's Supervisors and Admins can read this note. Members never see it.";
const NO_SHIFT = '';

interface SelfNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timezone: string;
  /** Editing an existing note (the project is locked). */
  note?: OwnNoteDto | null;
  /** Pre-selected project (the dashboard selection) when eligible. */
  initialProjectId?: string | null;
  /** Pre-selected shift (the dashboard's "Add note" while on shift). */
  initialShiftId?: string | null;
}

export function SelfNoteDialog({ open, onOpenChange, timezone, note = null, initialProjectId = null, initialShiftId = null }: SelfNoteDialogProps): JSX.Element {
  const projects = useOwnNoteProjects();
  const create = useCreateOwnNote();
  const update = useUpdateOwnNote();
  const today: LocalDate = todayIn(timezone);
  const [projectId, setProjectId] = useState('');
  const [date, setDate] = useState<string>(today);
  const [shiftId, setShiftId] = useState(NO_SHIFT);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const items = projects.data ?? [];

  // Reset only when the dialog opens or its target changes.
  const noteId = note?.id ?? null;
  useEffect(() => {
    if (!open) return;
    const eligible = initialProjectId !== null && items.some((p) => p.id === initialProjectId) ? initialProjectId : null;
    const first = items.length === 1 ? (items[0]?.id ?? '') : '';
    const pid = note?.project.id ?? eligible ?? first;
    setProjectId(pid);
    setDate(note?.date ?? today);
    setDescription(note?.description ?? '');
    const open_ = items.find((p) => p.id === pid)?.openShiftId ?? null;
    setShiftId(note ? (note.shift?.id ?? NO_SHIFT) : (initialShiftId ?? open_ ?? NO_SHIFT));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, noteId, initialProjectId, initialShiftId, projects.data]);

  const project = items.find((p) => p.id === projectId) ?? null;
  const shiftEnabled = project?.shiftEnabled === true;
  const shiftOptions = useOwnShiftOptions(projectId || null, date, open && shiftEnabled);
  const storedShift = note?.shift ?? null;
  const shiftChoices = [...(shiftOptions.data ?? []), ...(storedShift && !(shiftOptions.data ?? []).some((s) => s.id === storedShift.id) ? [storedShift] : [])];
  const trimmed = description.trim();
  const pending = create.isPending || update.isPending;

  function changeProject(id: string): void {
    setProjectId(id);
    const p = items.find((x) => x.id === id);
    setShiftId(date === today ? (p?.openShiftId ?? NO_SHIFT) : NO_SHIFT);
  }

  function changeDate(value: string): void {
    setDate(value);
    // Another day: the open shift no longer applies; the options for that day are offered instead.
    setShiftId(value === today ? (project?.openShiftId ?? NO_SHIFT) : NO_SHIFT);
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!projectId) return setError('Choose a project.');
    if (!date) return setError('Choose a date.');
    if (date > today) return setError(FUTURE_DATE_MESSAGE);
    if (trimmed.length === 0) return setError('Write a note.');
    const onError = (err: Error): void => setError(err instanceof ApiClientError ? err.message : 'Something went wrong. Please try again.');
    const chosenShift = shiftEnabled && shiftId !== NO_SHIFT ? shiftId : null;
    if (note) {
      const patch = {
        ...(date !== note.date ? { date } : {}),
        ...(trimmed !== note.description ? { description: trimmed } : {}),
        ...(chosenShift !== (note.shift?.id ?? null) ? { shiftId: chosenShift } : {}),
      };
      if (Object.keys(patch).length === 0) return onOpenChange(false);
      update.mutate({ noteId: note.id, patch }, { onSuccess: () => (toast.success('Note saved.'), onOpenChange(false)), onError });
      return;
    }
    // Always explicit: the dialog already shows the open shift as the default (or "No shift").
    create.mutate({ projectId, date, description: trimmed, shiftId: chosenShift }, { onSuccess: () => (toast.success('Note added.'), onOpenChange(false)), onError });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{note ? 'Edit note' : 'Add note'}</DialogTitle>
          <DialogDescription>{project && !project.reviewed ? SELF_NOTE_UNREVIEWED_HINT : SELF_NOTE_REVIEWED_HINT}</DialogDescription>
        </DialogHeader>
        <form id="self-note-form" onSubmit={onSubmit} className="space-y-3" noValidate>
          <div className="grid gap-1.5">
            <Label htmlFor="self-note-project">Project</Label>
            {note ? (
              <Input id="self-note-project" value={note.project.name} readOnly className="bg-muted" />
            ) : (
              <NativeSelect id="self-note-project" value={projectId} onChange={(e) => changeProject(e.target.value)}>
                <option value="">Choose a project</option>
                {items.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="self-note-date">Date</Label>
            <Input id="self-note-date" type="date" max={today} value={date} onChange={(e) => changeDate(e.target.value)} />
          </div>
          {shiftEnabled && (
            <div className="grid gap-1.5">
              <Label htmlFor="self-note-shift">Shift</Label>
              <NativeSelect id="self-note-shift" value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
                <option value={NO_SHIFT}>No shift</option>
                {shiftChoices.map((s) => (
                  <option key={s.id} value={s.id}>
                    {timeRange(s.startedAt, s.endedAt, timezone).replace('–', ' – ')}
                    {s.voided ? ' (voided)' : ''}
                  </option>
                ))}
              </NativeSelect>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="self-note-description">Description</Label>
            <textarea
              id="self-note-description"
              rows={5}
              maxLength={NOTE_MAX_LENGTH}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              aria-describedby="self-note-counter"
              className="flex w-full resize-y rounded-lg border border-input bg-field px-3 py-2 text-base leading-6 text-foreground placeholder:text-faint transition-colors duration-150 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring sm:text-sm sm:leading-5"
            />
            <p id="self-note-counter" className={cn('text-right text-xs tabular-nums', description.length >= NOTE_MAX_LENGTH ? 'text-warning-fg' : 'text-muted-foreground')}>
              {description.length} / {NOTE_MAX_LENGTH}
            </p>
          </div>
          {error && (
            <p role="alert" className="text-sm text-danger-fg">
              {error}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button type="submit" form="self-note-form" className="flex-1" disabled={pending}>
            {pending ? 'Saving…' : note ? 'Save' : 'Add note'}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
